import { FILTERS_NAME } from '@va/constants';
import { useGetWebsiteContributors } from '@va/dashboard/api-client/contributors.api';
import { useAutocompleteReferrersSuggestions } from '@va/dashboard/api-client/traffic-channels';
import { useTranslate } from '@va/localization';
import { MultiSelectFilter, TextFilter } from '@va/shared/feature-filters';
import { ApiOperators, FilterOperators } from '@va/types/filters';
import { useMemo } from 'react';
import { FilterHook } from '../types';

export const useNewReferrerFilter: FilterHook<typeof TextFilter> = (params) => {
  const translate = useTranslate();

  const filter = useMemo(
    () =>
      new TextFilter({
        id: params?.name ?? FILTERS_NAME.REFERRER_URL,
        name: FILTERS_NAME.REFERRER_URL,
        label: translate('filter.referrerUrl'),
        allowMultiSelect: true,
        operator: FilterOperators.is,
        multiSelectButtonLabel: translate('label.addNewUrl'),
        operatorOptions: [FilterOperators.is, FilterOperators.contains, FilterOperators.doesNotContain],
        operatorMapper: (operator) => {
          switch (operator) {
            case FilterOperators.contains:
              return ApiOperators.in;
            case FilterOperators.doesNotContain:
              return ApiOperators.notIn;
            case FilterOperators.is:
              return ApiOperators.eq;
            default:
              return ApiOperators.in;
          }
        },
        autocompleteOptionsFetcher: useAutocompleteReferrersSuggestions,
        ...params,
        inputProps: {
          placeholder: translate('filters.placeholder.referrerUrl'),
          ...params?.inputProps,
        },
      }),
    [params, translate],
  );

  return filter;
};

export const useAuthorFilter: FilterHook<typeof MultiSelectFilter> = (params) => {
  const translate = useTranslate();
  const { data, isLoading } = useGetWebsiteContributors();

  const options = useMemo(() => {
    if (!data) return [];
    return [
      {
        label: data.owner.fullName || data.owner.email,
        value: data.owner.id,
      },
    ].concat(
      data?.contributors.map((contributor) => ({
        label: contributor.fullName || contributor.email,
        value: contributor.id,
      })),
    );
  }, [data]);

  const filter = useMemo(
    () =>
      new MultiSelectFilter({
        id: 'createdBy',
        name: 'createdBy',
        label: translate('all.filters.author'),
        appliedLabel: translate('filters.applied.author'),
        ...params,
        inputProps: {
          useOptions: () => ({
            data: options,
            isLoading: isLoading,
          }),
          ...params?.inputProps,
        },
      }),
    [translate, params, options, isLoading],
  );
  return filter;
};
